import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { searchService } from '../../services/search';
import { SearchResult, SpeciesOption } from '../../types';

interface SpeciesInsight {
  species: string;
  declineReasons: string[];
  advantages: string[];
  disadvantages: string[];
  source?: string;
  evidence?: {
    matched_records?: number;
    dominant_region?: string;
    dominant_region_share_percent?: number;
    medium_high_risk_share_percent?: number;
    avg_confidence_percent?: number;
    year_span?: string;
  };
}

const getSpeciesInsight = (item: SearchResult): SpeciesInsight => {
  const apiInsight = item.species_insight;

  if (apiInsight) {
    return {
      species: item.species,
      declineReasons: apiInsight.decline_reasons || [],
      advantages: apiInsight.advantages || [],
      disadvantages: apiInsight.disadvantages || [],
      source: apiInsight.knowledge_source,
      evidence: apiInsight.evidence,
    };
  }

  return {
    species: item.species,
    declineReasons: [
      'Data-backed decline reasons are unavailable for this species in the current response.',
      'Try re-running search with a more specific species name for higher precision.',
    ],
    advantages: ['Biodiversity contribution details are currently unavailable.'],
    disadvantages: ['Ecosystem impact details are currently unavailable.'],
  };
};

const FALLBACK_SPECIES_OPTIONS: SpeciesOption[] = [
  { value: 'Apis mellifera', label: 'Apis mellifera', count: 0, is_unresolved: false },
  { value: 'Bombus terrestris', label: 'Bombus terrestris', count: 0, is_unresolved: false },
  { value: 'Pieris rapae', label: 'Pieris rapae', count: 0, is_unresolved: false },
  { value: 'Vanessa atalanta', label: 'Vanessa atalanta', count: 0, is_unresolved: false },
  { value: 'Papilio xuthus', label: 'Papilio xuthus', count: 0, is_unresolved: false },
  { value: 'Danaus plexippus', label: 'Danaus plexippus', count: 0, is_unresolved: false },
  { value: 'Bombus impatiens', label: 'Bombus impatiens', count: 0, is_unresolved: false },
  { value: 'Vanessa cardui', label: 'Vanessa cardui', count: 0, is_unresolved: false },
];

const SearchPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [smartQuery, setSmartQuery] = useState('');
  const [region, setRegion] = useState('All Regions');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [locationQuery, setLocationQuery] = useState('');
  const [species, setSpecies] = useState('All Species');
  const [yearFrom, setYearFrom] = useState('2021');
  const [yearTo, setYearTo] = useState('2026');
  const [limit, setLimit] = useState('100');
  const [includeFallback, setIncludeFallback] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState<SpeciesInsight | null>(null);
  const [speciesOptions, setSpeciesOptions] = useState<SpeciesOption[]>(FALLBACK_SPECIES_OPTIONS);
  const [locationMatchMode, setLocationMatchMode] = useState('strict');

  type SearchOverrides = {
    smartQuery: string;
    region: string;
    country: string;
    state: string;
    locationQuery: string;
    species: string;
    yearFrom: string;
    yearTo: string;
    limit: string;
    includeFallback: boolean;
    autoRetryRelaxed: boolean;
  };

  const executeSearch = useCallback(async (overrides?: Partial<SearchOverrides>) => {
    const nextSmartQuery = overrides?.smartQuery ?? smartQuery;
    const nextRegion = overrides?.region ?? region;
    const nextCountry = overrides?.country ?? country;
    const nextState = overrides?.state ?? state;
    const nextLocationQuery = overrides?.locationQuery ?? locationQuery;
    const nextSpecies = overrides?.species ?? species;
    const nextYearFrom = overrides?.yearFrom ?? yearFrom;
    const nextYearTo = overrides?.yearTo ?? yearTo;
    const nextLimit = overrides?.limit ?? limit;
    const nextIncludeFallback = overrides?.includeFallback ?? includeFallback;
    const autoRetryRelaxed = overrides?.autoRetryRelaxed ?? true;

    setError('');

    const from = Number(nextYearFrom);
    const to = Number(nextYearTo);
    if (Number.isFinite(from) && Number.isFinite(to) && from > to) {
      setError('Year From cannot be greater than Year To.');
      setSubmitted(true);
      return;
    }

    setLoading(true);

    try {
      const response = await searchService.search({
        smart_query: nextSmartQuery.trim() || undefined,
        region: nextRegion === 'All Regions' ? undefined : nextRegion,
        country: nextCountry.trim() || undefined,
        state: nextState.trim() || undefined,
        location_query: nextLocationQuery.trim() || undefined,
        species: nextSpecies === 'All Species' ? undefined : nextSpecies,
        include_fallback: nextIncludeFallback,
        year_from: Number(nextYearFrom) || undefined,
        year_to: Number(nextYearTo) || undefined,
        limit: Number(nextLimit) || 100,
      });

      setResults(response.results);
      setTotal(response.total);
      setLocationMatchMode(response.filters_applied?.location_match_mode || 'strict');
      setSubmitted(true);

      if (response.total === 0 && !nextIncludeFallback && autoRetryRelaxed) {
        setIncludeFallback(true);
        await executeSearch({
          smartQuery: nextSmartQuery,
          region: nextRegion,
          country: nextCountry,
          state: nextState,
          locationQuery: nextLocationQuery,
          species: nextSpecies,
          yearFrom: nextYearFrom,
          yearTo: nextYearTo,
          limit: nextLimit,
          includeFallback: true,
          autoRetryRelaxed: false,
        });
      }
    } catch (err: any) {
      setResults([]);
      setTotal(0);
      setLocationMatchMode('strict');
      setSubmitted(true);
      if (err?.response?.status === 401) {
        setError('Please sign in to access search results.');
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Failed to run search. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [country, includeFallback, limit, locationQuery, region, smartQuery, species, state, yearFrom, yearTo]);

  const runSearch = useCallback(async () => {
    await executeSearch();
  }, [executeSearch]);

  useEffect(() => {
    if (hasAutoLoaded) {
      return;
    }
    setHasAutoLoaded(true);
    void runSearch();
  }, [hasAutoLoaded, runSearch]);

  useEffect(() => {
    const loadSpeciesOptions = async () => {
      try {
        const response = await searchService.getSpeciesOptions(300);
        if (response.results.length > 0) {
          setSpeciesOptions(response.results);
        }
      } catch {
        setSpeciesOptions(FALLBACK_SPECIES_OPTIONS);
      }
    };

    void loadSpeciesOptions();
  }, []);

  useEffect(() => {
    if (!selectedInsight) {
      return;
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedInsight(null);
      }
    };

    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [selectedInsight]);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    await runSearch();
  };

  const onReset = () => {
    setSmartQuery('');
    setRegion('All Regions');
    setCountry('');
    setState('');
    setLocationQuery('');
    setSpecies('All Species');
    setYearFrom('2021');
    setYearTo('2026');
    setLimit('100');
    setIncludeFallback(false);
    setError('');
    setResults([]);
    setTotal(0);
    setLocationMatchMode('strict');
    setSubmitted(false);
    setSelectedInsight(null);
  };

  const applyBroaderPreset = async (preset: 'location' | 'years' | 'all') => {
    const currentYear = String(new Date().getUTCFullYear());

    if (preset === 'location') {
      setRegion('All Regions');
      setCountry('');
      setState('');
      setLocationQuery('');
      await executeSearch({
        region: 'All Regions',
        country: '',
        state: '',
        locationQuery: '',
        includeFallback: true,
        autoRetryRelaxed: false,
      });
      return;
    }

    if (preset === 'years') {
      setYearFrom('1990');
      setYearTo(currentYear);
      setLimit('200');
      await executeSearch({ yearFrom: '1990', yearTo: currentYear, limit: '200', includeFallback: true, autoRetryRelaxed: false });
      return;
    }

    setSmartQuery('');
    setRegion('All Regions');
    setCountry('');
    setState('');
    setLocationQuery('');
    setSpecies('All Species');
    setYearFrom('1990');
    setYearTo(currentYear);
    setLimit('200');
    setIncludeFallback(true);
    await executeSearch({
      smartQuery: '',
      region: 'All Regions',
      country: '',
      state: '',
      locationQuery: '',
      species: 'All Species',
      yearFrom: '1990',
      yearTo: currentYear,
      limit: '200',
      includeFallback: true,
      autoRetryRelaxed: false,
    });
  };

  const activeFilters = [
    Boolean(smartQuery.trim()),
    region !== 'All Regions',
    Boolean(country.trim()),
    Boolean(state.trim()),
    Boolean(locationQuery.trim()),
    species !== 'All Species',
    yearFrom !== '2021' || yearTo !== '2026',
    limit !== '100',
  ].filter(Boolean).length;

  const searchScopeSummary = useMemo(() => {
    const parts = [
      `Source: GBIF + OpenWeather + NASA`,
      region !== 'All Regions' ? `Region: ${region}` : 'Region: All',
      species !== 'All Species' ? `Species: ${species}` : 'Species: All',
      country.trim() ? `Country: ${country.trim()}` : null,
      state.trim() ? `State: ${state.trim()}` : null,
      locationQuery.trim() ? `Locality: ${locationQuery.trim()}` : null,
      smartQuery.trim() ? `Query: ${smartQuery.trim()}` : null,
      `Years: ${yearFrom}-${yearTo}`,
      `Limit: Top ${limit}`,
      includeFallback ? 'Mode: Relaxed blend' : 'Mode: Strict filters',
    ].filter(Boolean) as string[];

    return parts;
  }, [country, includeFallback, limit, locationQuery, region, smartQuery, species, state, yearFrom, yearTo]);

  return (
    <section className="feature-page search-premium">
      <div className="search-hero">
        <div className="search-hero-copy">
          <p className="search-kicker">Discovery Explorer</p>
          <h1>Insect Biodiversity Search</h1>
          <p>Find insect records by region, species, and year.</p>
        </div>

        <div className="search-hero-metrics" aria-label="Search overview">
          <article className="search-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◌</span> Total Matches</h3>
            <p>{total}</p>
          </article>
          <article className="search-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◍</span> Shown</h3>
            <p>{results.length}</p>
          </article>
          <article className="search-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◈</span> Active Filters</h3>
            <p>{activeFilters}</p>
          </article>
          <article className="search-hero-tile">
            <h3><span className="hero-tile-icon" aria-hidden="true">◎</span> Access</h3>
            <p>{isAuthenticated ? 'Signed In' : 'Guest'}</p>
          </article>
        </div>
      </div>

      <div className="search-card">
        <div className="card-title-row">
          <h2>
            <span className="title-icon">⎇</span>
            Search
          </h2>
          <span className="card-collapse" aria-hidden="true">
            ⌃
          </span>
        </div>

        <form onSubmit={onSubmit} className="filter-form">
          <div className="filter-grid">
            <label>
              <span className="filter-label">⌕ Smart Query</span>
              <input
                value={smartQuery}
                onChange={(e) => setSmartQuery(e.target.value)}
                type="text"
                placeholder="species, country, locality, risk"
              />
            </label>

            <label>
              <span className="filter-label">◉ Region</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="All Regions">All Regions</option>
                <option value="Asia">Asia</option>
                <option value="Europe">Europe</option>
                <option value="North America">North America</option>
                <option value="South America">South America</option>
                <option value="Oceania">Oceania</option>
                <option value="Africa">Africa</option>
              </select>
            </label>

            <label>
              <span className="filter-label">◍ Country</span>
              <input
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                type="text"
                placeholder="e.g. India"
              />
            </label>

            <label>
              <span className="filter-label">◎ State</span>
              <input
                value={state}
                onChange={(e) => setState(e.target.value)}
                type="text"
                placeholder="e.g. Maharashtra"
              />
            </label>

            <label>
              <span className="filter-label">⌖ Locality</span>
              <input
                value={locationQuery}
                onChange={(e) => setLocationQuery(e.target.value)}
                type="text"
                placeholder="city, district, village"
              />
            </label>

            <label>
              <span className="filter-label">⚛ Species</span>
              <select value={species} onChange={(e) => setSpecies(e.target.value)}>
                <option value="All Species">All Species</option>
                {speciesOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span className="filter-label">⌚ Year From</span>
              <input value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} type="number" min={1900} max={2100} />
            </label>

            <label>
              <span className="filter-label">⌚ Year To</span>
              <input value={yearTo} onChange={(e) => setYearTo(e.target.value)} type="number" min={1900} max={2100} />
            </label>

            <label>
              <span className="filter-label">⎘ Result Limit</span>
              <select value={limit} onChange={(e) => setLimit(e.target.value)}>
                <option value="25">Top 25</option>
                <option value="50">Top 50</option>
                <option value="100">Top 100</option>
                <option value="200">Top 200</option>
              </select>
            </label>

            <label className="search-mode-toggle">
              <span className="filter-label">⚙ Search Mode</span>
              <button
                type="button"
                className={`mode-toggle ${includeFallback ? 'active' : ''}`}
                onClick={() => setIncludeFallback((value) => !value)}
                aria-pressed={includeFallback}
              >
                {includeFallback ? 'Relaxed source blend' : 'Strict filters only'}
              </button>
              <small>Relaxed mode allows broader GBIF matches and blends OpenWeather + NASA signals.</small>
            </label>

          </div>

          <div className="action-row">
            <button type="submit" disabled={loading}>
              {loading ? 'Searching...' : '⌕ Search'}
            </button>
            <button type="button" className="ghost" onClick={onReset}>
              ✕ Reset
            </button>
          </div>
        </form>
      </div>

      {error && <p className="form-error">{error}</p>}

      {submitted && (
        <div className="results-card">
          <h3>Search Results ({results.length} of {total})</h3>
          <p className="results-mode-note">
            Match mode: {locationMatchMode === 'fallback-broadest'
              ? 'Broad fallback only, no exact filter match'
              : locationMatchMode.startsWith('relaxed')
                ? 'Relaxed filter match'
                : locationMatchMode === 'strict-empty'
                  ? 'No exact matches'
                  : includeFallback
                    ? 'Strict filters with relaxed blend enabled'
                    : 'Exact filter match'}
          </p>
          <div className="results-summary-strip" aria-label="Search summary">
            {searchScopeSummary.map((item) => (
              <span key={item} className="results-summary-pill">
                {item}
              </span>
            ))}
          </div>
          {results.length === 0 && !error ? (
            <div className="no-results">
              <p>No records matched your filters.</p>
              <div className="no-results-suggestions">
                <p>Try a wider GBIF search scope:</p>
                <div className="no-results-actions">
                  <button type="button" className="ghost" onClick={() => { setIncludeFallback(true); void executeSearch({ includeFallback: true, autoRetryRelaxed: false }); }}>
                    Enable Relaxed Blend
                  </button>
                  <button type="button" className="ghost" onClick={() => { setRegion('All Regions'); void executeSearch({ region: 'All Regions' }); }}>
                    Clear Region
                  </button>
                  <button type="button" className="ghost" onClick={() => { void applyBroaderPreset('location'); }}>
                    Clear Location Filters
                  </button>
                  <button type="button" className="ghost" onClick={() => { void applyBroaderPreset('years'); }}>
                    Widen Year Range
                  </button>
                  <button type="button" className="ghost" onClick={() => { void applyBroaderPreset('all'); }}>
                    Apply All Relaxations
                  </button>
                </div>
                <p>Preset applies and searches immediately.</p>
              </div>
            </div>
          ) : (
            <div className="results-grid">
              {results.map((item) => {
                const statusValue = item.population_status || 'Unknown';
                const isFallbackRecord = item.prediction_id.startsWith('gbif-');
                const confidenceLabel = isFallbackRecord ? 'N/A' : `${(item.confidence * 100).toFixed(1)}%`;
                const hasCoordinates = typeof item.latitude === 'number' && typeof item.longitude === 'number';
                const mapsUrl = hasCoordinates
                  ? `https://www.google.com/maps?q=${item.latitude},${item.longitude}`
                  : null;
                const stateLabel = item.state && item.state !== 'Unknown' ? `, ${item.state}` : '';
                const localityLabel = item.locality ? `, ${item.locality}` : '';
                const env = item.environmental_summary;
                const envAvailable = typeof env?.temperature_celsius === 'number' || typeof env?.ndvi === 'number';
                const envSourceLabel = env?.sources?.openweather || env?.sources?.nasa_earthdata ? 'Blended source signals' : 'GBIF only';

                return (
                  <div key={item.prediction_id} className="result-item">
                    <div className="result-header">
                      <h4 className="result-species">{item.species}</h4>
                      <span className="result-confidence">{confidenceLabel}</span>
                    </div>

                    <div className="result-meta-row">
                      <span className="result-meta-pill">{isFallbackRecord ? 'GBIF' : 'User Prediction'}</span>
                      <span className="result-meta-pill">{item.year ?? 'Year N/A'}</span>
                      <span className="result-meta-pill result-meta-pill-score">
                        Relevance {Math.max(0, Math.round(item.relevance_score ?? 0))}
                      </span>
                    </div>

                    <div className="result-details">
                      <div className="detail-row">
                        <span className="detail-label">Location</span>
                        <span className="detail-value">{item.country}{stateLabel}{localityLabel} ({item.region})</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Status</span>
                        <span className={`status-badge status-${statusValue.toLowerCase()}`}>
                          {statusValue}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Pinpoint</span>
                        <span className="detail-value">
                          {hasCoordinates ? `${item.latitude?.toFixed(4)}, ${item.longitude?.toFixed(4)}` : 'Not available'}
                        </span>
                      </div>
                      {envAvailable && (
                        <div className="result-env-block">
                          <div className="detail-row">
                            <span className="detail-label">Env Signals</span>
                            <span className="detail-value result-env-source">{envSourceLabel}</span>
                          </div>
                          <div className="result-env-chips">
                            <span className="result-env-chip">
                              Temp {typeof env?.temperature_celsius === 'number' ? env.temperature_celsius.toFixed(1) : 'N/A'} C
                            </span>
                            <span className="result-env-chip">
                              Humidity {typeof env?.humidity_percent === 'number' ? env.humidity_percent.toFixed(0) : 'N/A'}%
                            </span>
                            <span className="result-env-chip">
                              Rain {typeof env?.rainfall_mm === 'number' ? env.rainfall_mm.toFixed(1) : 'N/A'} mm
                            </span>
                            <span className="result-env-chip result-env-chip-accent">
                              NDVI {typeof env?.ndvi === 'number' ? env.ndvi.toFixed(2) : 'N/A'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {mapsUrl && (
                      <a href={mapsUrl} target="_blank" rel="noreferrer" className="result-map-link">
                        Open Pinpoint on Map
                      </a>
                    )}

                    <div className="result-footer-strip">
                      <span>{isFallbackRecord ? 'GBIF search record' : 'User-generated prediction'}</span>
                      <span>{item.year ?? 'Year N/A'}</span>
                      <span>{item.region}</span>
                    </div>

                    <button
                      type="button"
                      className="result-insight-btn"
                      onClick={() => setSelectedInsight(getSpeciesInsight(item))}
                    >
                      View Species Insight
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selectedInsight && (
        <div className="species-insight-overlay" role="presentation" onClick={() => setSelectedInsight(null)}>
          <section
            className="species-insight-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="species-insight-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="species-insight-header">
              <div>
                <h3 id="species-insight-title">{selectedInsight.species}</h3>
                {selectedInsight.source && (
                  <p className="species-insight-source">Source: {selectedInsight.source}</p>
                )}
              </div>
              <button type="button" className="species-insight-close" onClick={() => setSelectedInsight(null)} aria-label="Close species insight">
                x
              </button>
            </div>

            <div className="species-insight-section">
              <h4>Why the species is declining</h4>
              <ul>
                {selectedInsight.declineReasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>

            <div className="species-insight-section">
              <h4>Advantages</h4>
              <ul>
                {selectedInsight.advantages.map((advantage) => (
                  <li key={advantage}>{advantage}</li>
                ))}
              </ul>
            </div>

            <div className="species-insight-section">
              <h4>Disadvantages</h4>
              <ul>
                {selectedInsight.disadvantages.map((disadvantage) => (
                  <li key={disadvantage}>{disadvantage}</li>
                ))}
              </ul>
            </div>

            {selectedInsight.evidence && (
              <div className="species-insight-section">
                <h4>Evidence Snapshot</h4>
                <div className="species-evidence-grid">
                  {typeof selectedInsight.evidence.matched_records === 'number' && (
                    <p className="species-evidence-item">
                      <strong>Matched Records:</strong> {selectedInsight.evidence.matched_records}
                    </p>
                  )}
                  {selectedInsight.evidence.year_span && (
                    <p className="species-evidence-item">
                      <strong>Year Span:</strong> {selectedInsight.evidence.year_span}
                    </p>
                  )}
                  {selectedInsight.evidence.dominant_region && (
                    <p className="species-evidence-item">
                      <strong>Dominant Region:</strong> {selectedInsight.evidence.dominant_region}
                    </p>
                  )}
                  {typeof selectedInsight.evidence.dominant_region_share_percent === 'number' && (
                    <p className="species-evidence-item">
                      <strong>Region Share:</strong> {selectedInsight.evidence.dominant_region_share_percent}%
                    </p>
                  )}
                  {typeof selectedInsight.evidence.medium_high_risk_share_percent === 'number' && (
                    <p className="species-evidence-item">
                      <strong>Medium/High Risk Share:</strong> {selectedInsight.evidence.medium_high_risk_share_percent}%
                    </p>
                  )}
                  {typeof selectedInsight.evidence.avg_confidence_percent === 'number' && (
                    <p className="species-evidence-item">
                      <strong>Average Confidence:</strong> {selectedInsight.evidence.avg_confidence_percent}%
                    </p>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </section>
  );
};

export default SearchPage;
